/** Entity-profile LINKED RECORDS card (secondary column) — spec
 * `docs/specs/strategic-architecture/entity-profile-linked-records-spec.md` §8.
 *
 * **A linked record is an active relationship FACT whose `to` endpoint is a list entry the caller may
 * see.** This card replaced `CrmLinkedDeals`, which matched emails/domains/names against the frozen
 * Affinity snapshot and labelled each row with WHICH HEURISTIC FIRED. Nothing here guesses:
 *
 * - **Groups come from the payload, one per list, headed by the list's CONFIGURED label** — a list the
 *   caller cannot access is absent from the payload entirely (no group, no name, no count), so this
 *   file has no notion of "a list you can't open" to render;
 * - **roles are rendered from the vocabulary labels in the payload** — the FE holds NO role
 *   vocabulary and never maps a `relationship_type` to prose;
 * - **order is the payload's** (salience, then record recency, then entry id — resolved server-side
 *   from vocabulary data). This card does not sort, and must not: re-sorting here would silently
 *   replace a governed ranking with a rendering accident;
 * - **evidence provenance replaces the `matched: domain` chip** — supporting message count, the
 *   evidenced window, and who decided: `confirmed by <name>` for a human ruling, the producer class
 *   otherwise;
 * - **a PROPOSED row renders name + lifecycle + roles only.** The payload enforces that cap (§4's D8
 *   ruling), and this card renders only what it is given — it never reaches for a field value.
 *
 * **Empty is one honest line**, and there is deliberately no "unconfirmed matches" affordance, no
 * toggle and no fallback: an empty card means the ledger holds no active fact (§6). */
import { useState } from 'react';
import { ApiError } from '../../api/client';
import { navigateTab } from '../../hooks/useTabRouter';
import { useCrmLinkedRecords, type CrmEntityKind } from '../../hooks/useCrm';
import type { CrmLinkedRecordGroup, CrmLinkedRecordRole, CrmLinkedRecordRow } from '../../types';
import { FactWhyButton } from './CrmFactEvidencePopover';
import { cellText } from './crmCells';
import { fmtShortDate } from './crmFormat';
import shell from './crm.module.css';
import styles from './CrmLinkedRecords.module.css';

interface Props {
  kind: CrmEntityKind;
  id: number;
}

/** The row's one-line provenance. Every branch reads off the payload — `decided_via`/`decided_by` and
 * the producer class are the server's words, not a local interpretation of them.
 *
 * `decided_by: null` with `decided_via: 'manual'` is a REAL state, not missing data: a human ruled and
 * the caller may not see who (§4's entity-side symmetry). It renders as "confirmed by a colleague" —
 * the ruling is reported, the party is not named. */
function evidenceLine(row: CrmLinkedRecordRow): string {
  const ev = row.evidence;
  const parts: string[] = [];
  if (ev.messages > 0) parts.push(`${ev.messages} message${ev.messages === 1 ? '' : 's'}`);
  const first = ev.first_at ? fmtShortDate(ev.first_at) : null;
  const last = ev.last_at ? fmtShortDate(ev.last_at) : null;
  if (first && last) parts.push(first === last ? first : `${first} – ${last}`);
  else if (first || last) parts.push((first ?? last) as string);
  if (ev.decided_via === 'manual') {
    parts.push(ev.decided_by ? `confirmed by ${ev.decided_by}` : 'confirmed by a colleague');
  } else if (ev.producers.length > 0) {
    parts.push(`via ${ev.producers.join(', ')}`);
  } else {
    // A machine fact whose producer the ledger did not name. "machine-inferred" is what is actually
    // known — "no decision recorded" would read as "nothing believes this", which is false.
    parts.push('machine-inferred');
  }
  return parts.join(' · ');
}

/** Where a row click goes: the record panel over the row's own list, in-tab, exactly as
 * `PendingUpdateBadge` does — `sel=list:<id>` + `rec=<entry>`. This is deliberately the ONLY
 * destination (owner ruling, 2026-08-21): a `deal_ref` row used to hop to the Deals tab, but linked
 * records must only ever point at the IOT panel view of a deal — the record panel is where the record
 * lives for every caller regardless of tab visibility, so the hop's `/api/tabs` guard went with it.
 *
 * `return_to` is NOT passed — it is a cross-tab affordance param (`TAB_PARAMS.crm` does not carry it),
 * and writing it on an in-tab hop would leave a param no page reads in a shareable URL. */
function openLinkedRecord(group: CrmLinkedRecordGroup, row: CrmLinkedRecordRow): void {
  navigateTab('crm', { params: { sel: `list:${group.list_id}`, rec: String(row.entry_id) } });
}

/**
 * The row's role chips, with the IMPLIED ones behind a "+N implied" toggle (party-facts spec §5
 * rule 2). Holland & Knight holds a service-provider fact, a bare correspondence fact and a role-less
 * participation fact on one deal: three chips saying one thing, and the two weaker ones are folded.
 *
 * ⚠ **THE SERVER DECIDES WHICH.** `implied` is computed from the governed register's `implied_by`
 * over the row's OWN facts (`relationship_vocabulary.implied_chip_flags`), never from a rule here —
 * this card holds no role vocabulary and must not acquire one. A payload with no `implied` on any role
 * (the flag off, or a client that predates it) folds nothing and renders exactly as it did before.
 *
 * ⚠ **Hidden is not withheld.** The chips are on the wire, the facts are active, their evidence is
 * already counted in the row's provenance line, and the ROW always renders — folding is per chip,
 * never per row. So the toggle states the count rather than swallowing it: a reader can always see
 * that something was folded and open it, which is the same "never indistinguishable from withheld"
 * discipline the two gates follow.
 *
 * Local `useState`, not URL state: an expander inside a row is transient view state, not a deep link.
 */
function RowRoles({ roles }: { roles: CrmLinkedRecordRole[] }) {
  const [expanded, setExpanded] = useState(false);
  // `implied !== true` rather than `!implied`: a role with the key ABSENT (flag off, or a payload
  // that predates it) is not implied, and must never be folded on a guess.
  const standing = roles.filter((role) => role.implied !== true);
  const shown = expanded ? roles : standing;
  const hidden = roles.length - standing.length;
  return (
    <div className={styles.roles}>
      {shown.map((role, i) => (
        <span
          key={`${role.relationship_type}-${i}`}
          className={role.implied === true ? `${styles.role} ${styles.roleImplied}` : styles.role}
        >
          {role.label}
        </span>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          className={styles.impliedToggle}
          aria-expanded={expanded}
          // The chips sit inside a row that is itself a button (it opens the record), so the click
          // must not travel: expanding a label is not asking to navigate away from it.
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {expanded ? 'less' : `+${hidden} implied`}
        </button>
      )}
    </div>
  );
}

export function CrmLinkedRecords({ kind, id }: Props) {
  const q = useCrmLinkedRecords(kind, id);
  // ORDER IS THE PAYLOAD'S — groups and rows are consumed as delivered (see the header note).
  // ⚠ A 404 IS "NOTHING TO SHOW", NOT A FAILURE, and this is load-bearing (adversarial review,
  // 2026-08-10). The endpoint is existence-safe: it 404s for an entity the caller may not see AND
  // while `crm_linked_records_enabled` is off. Treating that as an error would turn the flag — the
  // documented kill switch for this surface — into "Failed to load linked records." on every person
  // and organization profile in the app, which is the opposite of a safe rollback.
  const notFound = q.error instanceof ApiError && q.error.status === 404;
  const failed = q.error != null && !notFound;
  const groups = q.data?.groups ?? [];
  // A group with no rows renders NOTHING — no heading, no count line. S1 never emits one (it drops a
  // group whose rows all fell to a gate), and this keeps that a property of the CARD too: "Showing 0
  // of 5" would be precisely the "5 records you can't see" disclosure §4 forbids.
  const shown = groups.filter((group) => group.rows.length > 0);
  const empty = !q.isLoading && !failed && shown.length === 0;
  return (
    <div className={shell.card}>
      <div className={shell.cardTitle}>Linked records</div>
      {q.isLoading && <div className={shell.loading}>Loading…</div>}
      {failed && <div className={shell.error}>Failed to load linked records.</div>}
      {empty && (
        <div className={shell.empty}>The relationship ledger holds no active fact for this entity.</div>
      )}
      {shown.map((group) => (
        <div key={group.list_id} className={styles.group}>
          <div className={styles.groupHead}>{group.label}</div>
          <table className={shell.sideTable}>
            <tbody>
              {group.rows.map((row) => {
                const go = () => openLinkedRecord(group, row);
                // ⛔ THE D8 CAP, ENFORCED A SECOND TIME (adversarial review, 2026-08-10). The payload
                // already strips a proposed row to name + lifecycle, and this card is where that
                // stripping is visible — so it declines the two value slots itself rather than
                // trusting the wire. A payload regression then renders nothing instead of a field
                // value the ruling withheld, and the test fixture below carries values to prove it.
                const proposed = row.lifecycle_status !== 'committed';
                const numeric = proposed ? '' : cellText(row.display.numeric);
                const secondary = proposed ? '' : row.display.secondary;
                return (
                  <tr
                    key={row.entry_id}
                    className={shell.dealRow}
                    role="button"
                    tabIndex={0}
                    onClick={go}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        go();
                      }
                    }}
                  >
                    <td>
                      <div className={shell.rowName}>
                        {row.display.primary || `Record ${row.entry_id}`}
                        {proposed && (
                          <span className={styles.lifecycle}>{row.lifecycle_status}</span>
                        )}
                      </div>
                      {row.roles.length > 0 && <RowRoles roles={row.roles} />}
                      {/* Absent on a proposed row — never a placeholder that hints at a field value
                          the D8 ruling withheld. */}
                      {secondary && <div className={shell.rowSub}>{secondary}</div>}
                      {/* WP-6b — the provenance line gains the "why?" popover, because "3 messages
                          · confirmed by Brad" is precisely the claim someone wants to open. The ids
                          are the row's OWN facts, present only while the trace flag is on, so a dark
                          payload renders exactly the line it always did. */}
                      <div className={styles.evidence}>
                        {evidenceLine(row)}
                        {(row.evidence.assertion_ids?.length ?? 0) > 0 && (
                          <FactWhyButton assertionIds={row.evidence.assertion_ids as number[]}
                                         label={row.display.primary || `Record ${row.entry_id}`} />
                        )}
                      </div>
                    </td>
                    <td className={shell.rowNum}>{numeric}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {group.row_count > group.rows.length && (
            <div className={styles.capped}>
              Showing {group.rows.length} of {group.row_count} linked records.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
